import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaChevronRight, FaEnvelope, FaPhone, FaSlidersH, FaUser } from "react-icons/fa";
import { Badge } from "./common";
import { errorMessage, fmtNum } from "../dmApi";
import { ResultsMobileCard, ResultsMobileList } from "../../../components/ResultsMobile";
import useCompactViewport from "../../../hooks/useCompactViewport";

const dash = <span className="dmp-muted">—</span>;

/**
 * Staff DM client search: name, business type and include-inactive filters over every
 * DM-enrolled client, with results in a table. Clicking a row opens the client.
 */
function ClientSearch({ api, onSelect }) {
  const [name, setName] = useState("");
  const [businessTypeId, setBusinessTypeId] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const isCompactViewport = useCompactViewport();
  const term = name.trim();

  useEffect(() => {
    api
      .businessTypes()
      .then((list) => setBusinessTypes(list || []))
      .catch(() => setBusinessTypes([]));
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      setError("");
      try {
        const res = await api.tenants(term, {
          all: true,
          businessTypeId: businessTypeId || null,
          includeInactive,
        });
        if (!cancelled) setRows(res?.enrolled || []);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Could not load clients"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, term ? 300 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [api, term, businessTypeId, includeInactive]);

  const hasFilters = Boolean(term || businessTypeId || includeInactive);

  return (
    <div className="dmp-client-search">
      {isCompactViewport ? (
        <div className={`dmp-client-search__compact co-filters-collapsible${filtersOpen ? " is-open" : ""}`}>
          <div className="co-filter-search-bar">
            <input
              type="text"
              className="co-filter-search-bar__input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Search company, contact, phone or email"
              aria-label="Search clients"
              autoComplete="off"
            />
            <button
              type="button"
              className={`co-filter-search-bar__icon${filtersOpen ? " is-active" : ""}`}
              onClick={() => setFiltersOpen((o) => !o)}
              aria-expanded={filtersOpen}
              aria-label={filtersOpen ? "Hide Filters" : "Show Filters"}
              title={filtersOpen ? "Hide Filters" : "Show Filters"}
            >
              <FaSlidersH aria-hidden />
            </button>
          </div>
        </div>
      ) : null}

      <div
        className={`dmp-filters${isCompactViewport ? " dmp-client-search__filters--compact" : ""}`}
        hidden={isCompactViewport && !filtersOpen}
      >
        {!isCompactViewport ? (
          <label className="dmp-field dmp-client-search__name">
            Name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Company, contact person, phone or email"
              autoComplete="off"
            />
          </label>
        ) : null}
        <label className="dmp-field dmp-client-search__type">
          Business Type
          <select value={businessTypeId} onChange={(e) => setBusinessTypeId(e.target.value)}>
            <option value="">All business types</option>
            {businessTypes.map((b) => (
              <option key={b.Domain_Id} value={b.Domain_Id}>
                {b.Domain_Name}
              </option>
            ))}
          </select>
        </label>
        <label className="dmp-field dmp-client-search__check">
          In-Active
          <input
            type="checkbox"
            className="dmp-client-search__check-box"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
        </label>
        {hasFilters ? (
          <button
            type="button"
            className="dmp-btn dmp-btn--ghost"
            onClick={() => {
              setName("");
              setBusinessTypeId("");
              setIncludeInactive(false);
            }}
          >
            Clear
          </button>
        ) : null}
      </div>

      {error ? <div className="dmp-error">{error}</div> : null}

      <ResultsMobileList
        loading={loading && rows.length === 0}
        loadingLabel="Loading clients…"
        emptyLabel={hasFilters ? "No clients match these filters." : "No clients are enrolled in Digital Marketing yet."}
      >
        {rows.map((t) => {
          const contact = t.Person_Name && t.Person_Name !== t.Name ? t.Person_Name : null;
          const services = t.Dm_Access?.services || [];
          const isActive = t.Status === "Active";
          return (
            <ResultsMobileCard
              key={t.tenant_id}
              title={t.Name}
              meta={[
                t.Status ? { label: t.Status, tone: isActive ? "success" : "danger" } : null,
                t.Business_Type
                  ? { label: t.Business_Model ? `${t.Business_Type} · ${t.Business_Model}` : t.Business_Type, tone: "info" }
                  : null,
              ].filter(Boolean)}
              onOpen={() => onSelect(t)}
              actions={
                <button type="button" className="dmp-client-card__open" onClick={() => onSelect(t)}>
                  Open client
                  <FaChevronRight aria-hidden="true" />
                </button>
              }
            >
              {contact || t.Phone || t.Email ? (
                <ul className="co-tcard__contact">
                  {contact ? (
                    <li>
                      <FaUser aria-hidden="true" />
                      <span>{contact}</span>
                    </li>
                  ) : null}
                  {t.Phone ? (
                    <li>
                      <FaPhone aria-hidden="true" style={{ transform: "scaleX(-1)" }} />
                      <span>{t.Phone}</span>
                    </li>
                  ) : null}
                  {t.Email ? (
                    <li>
                      <FaEnvelope aria-hidden="true" />
                      <span>{t.Email}</span>
                    </li>
                  ) : null}
                </ul>
              ) : null}
              <dl className="co-tcard__info">
                <div>
                  <dt>Projects</dt>
                  <dd>{fmtNum(t.Project_Count)}</dd>
                </div>
                <div className="co-tcard__info-full">
                  <dt>DM Services</dt>
                  <dd>
                    {services.length ? (
                      <span className="dmp-chips">
                        {services.map((s) => (
                          <Badge key={s} value="Organic">
                            {s}
                          </Badge>
                        ))}
                      </span>
                    ) : (
                      "—"
                    )}
                  </dd>
                </div>
              </dl>
            </ResultsMobileCard>
          );
        })}
      </ResultsMobileList>

      <div className="dmp-table-wrap co-desktop-table">
        <table className="dmp-table">
          <thead>
            <tr>
              <th>Company Name</th>
              <th>Business Type</th>
              <th>Contact Person</th>
              <th>Phone / Email</th>
              <th>DM Services</th>
              <th className="dmp-num">Projects</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="dmp-empty">
                  Loading clients…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="dmp-empty">
                  {hasFilters ? "No clients match these filters." : "No clients are enrolled in Digital Marketing yet."}
                </td>
              </tr>
            ) : (
              rows.map((t) => (
                <tr key={t.tenant_id} className="is-clickable" onClick={() => onSelect(t)}>
                  <td>
                    <strong>{t.Name}</strong>
                  </td>
                  <td>
                    {t.Business_Type || dash}
                    {t.Business_Model ? <div className="dmp-muted">{t.Business_Model} based</div> : null}
                  </td>
                  <td>{t.Person_Name && t.Person_Name !== t.Name ? t.Person_Name : dash}</td>
                  <td>
                    {t.Phone || dash}
                    {t.Email ? <div className="dmp-muted">{t.Email}</div> : null}
                  </td>
                  <td>
                    <span className="dmp-chips">
                      {(t.Dm_Access?.services || []).map((s) => (
                        <Badge key={s} value="Organic">
                          {s}
                        </Badge>
                      ))}
                    </span>
                  </td>
                  <td className="dmp-num">{fmtNum(t.Project_Count)}</td>
                  <td>
                    <Badge value={t.Status === "Active" ? "Active" : "Inactive"}>{t.Status || "—"}</Badge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="dmp-muted dmp-client-search__foot">
        {loading ? "Refreshing… " : `${rows.length} client${rows.length === 1 ? "" : "s"}. `}
        Client not listed? Digital Marketing is assigned from the tenant’s edit page (
        <Link to="/tenants">Tenants</Link> → Edit → services).
      </p>
    </div>
  );
}

export default ClientSearch;
